import {
  fetchEpaFuelEfficiencyRatingsByIds,
  type EpaFuelEfficiencyRatingsDTO,
} from "@/api/vtnz-backend/epaFuelEfficiencyRatings";
import {
  fetchFleetVehicles,
  getAllVehiclesLocations,
  getVehicleLocation,
  getVehiclesForLocation,
  updateVehicle as updateVehicleAPI,
  type VehicleLocationDTO,
  type VehicleUpdateDTO,
} from "@/api/vtnz-backend/vehicles";
import { useFleetStore } from "@/domains/fleet/fleetStore";
import { useUserStore } from "@/domains/user/userStore";
import type { FetchState } from "@/types/sharedTypes";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { Vehicle } from ".";
import { VehicleCore } from "./core/vehicleCoreClass";

export const useVehicleStore = defineStore("vehicleStore", () => {
  const userStore = useUserStore();
  const fleetStore = useFleetStore();

  // ---------------------------------- State --------------------------------- //

  const vehicles = ref<Vehicle[]>([]);

  /** indicates the vehicle store's current data fetching state */
  const fetchState = ref<FetchState>("idle");
  const fetchErrorMsg = ref<string | null>(null);

  /** reactive array holding each vehicle's latest known location */
  const vehicleLocations = ref<VehicleLocationDTO[]>([]);

  /** indicates the vehicle locations fetch's current state */
  const vehicleLocationsFetchState = ref<FetchState>("idle");
  const vehicleLocationsFetchErrorMsg = ref<string | null>(null);

  /**
   * Reactive array holding the fetched EPA fuel efficiency ratings relevant
   * to the current fleet (i.e. only the ids referenced by
   * `VehicleICE.epaFuelEfficiencyId` across `vehicles`, never the whole
   * ~51k-row table - see fetchEpaFuelEfficiencyRatings).
   */
  const epaFuelEfficiencyRatings = ref<EpaFuelEfficiencyRatingsDTO[]>([]);

  /** indicates the EPA fuel efficiency ratings fetch's current state */
  const epaFuelEfficiencyRatingsFetchState = ref<FetchState>("idle");
  const epaFuelEfficiencyRatingsFetchErrorMsg = ref<string | null>(null);

  // PoC-ONLY CONCEPT — DO NOT PORT AS-IS. The demo backend simulates live
  // vehicle movement by updating locations server-side every ~10s, and
  // there's no PoC time budget to stand up a websocket/push channel to
  // react to that, so this polls instead. See startLocationsPolling.
  const LOCATIONS_POLL_INTERVAL_MS = 10_000;
  const locationsPollHandle = ref<ReturnType<typeof setInterval> | null>(null);

  // PoC-ONLY HACK — DO NOT PORT AS-IS. Tracks whether
  // syncVehicleSourceFromLocations has already run once for the current
  // fleet, so it doesn't redo its (harmless but pointless) work on every
  // ~10s location poll tick. See that function for the full reasoning.
  const hasFakedVehicleSourceFromLocations = ref(false);

  // --------------------------------- Getters -------------------------------- //

  /** helper flag to indicate async fetching is in operation. Good for loading animations. */
  const isFetching = computed(() => fetchState.value === "loading");

  const defaultVehicle = computed(() =>
    vehicles.value.find(
      (v) => v.externalId === userStore.defaultVehicleExternalId,
    ),
  );

  const getConnectedVehicles = computed(() => {
    return vehicles.value.filter((v) => v.vehicleSource === "TELEMATICS");
  });

  const getCustomVehicles = computed(() => {
    return vehicles.value.filter((v) => v.vehicleSource === "CUSTOM");
  });

  const getGenericVehicles = computed(() => {
    return vehicles.value.filter((v) => v.vehicleSource === "GENERIC");
  });

  /** helper flag to indicate the vehicle locations fetch is in operation. Good for loading animations. */
  const isFetchingLocations = computed(
    () => vehicleLocationsFetchState.value === "loading",
  );

  /**
   * Returns the latest known location for the given vehicle, if one has
   * been fetched.
   *
   * `vehicleExternalId` is the vehicle's external id (`Vehicle.externalId`),
   * not a local/domain id.
   */
  function getLocationForVehicle(
    vehicleExternalId: string,
  ): VehicleLocationDTO | undefined {
    return vehicleLocations.value.find(
      (location) => location.vehicle_id === vehicleExternalId,
    );
  }

  /**
   * Looks up a vehicle by its external id - useful for translating a
   * backend-sourced id (e.g. from a notification's `associated_id`) into
   * the vehicle, since dashboardNavStore's `showVehicleDetails` keys off
   * the vehicle's local `.id`, not its `externalId`.
   */
  function getVehicleByExternalId(
    vehicleExternalId: string,
  ): Vehicle | undefined {
    return vehicles.value.find((v) => v.externalId === vehicleExternalId);
  }

  /**
   * Returns the EPA fuel efficiency rating for the given vehicle, if it has
   * a resolved `epaFuelEfficiencyId` and that rating has been fetched.
   *
   * `vehicleExternalId` is the vehicle's external id, not a local/domain id.
   */
  function getEpaFuelEfficiencyRatingForVehicle(
    vehicleExternalId: string,
  ): EpaFuelEfficiencyRatingsDTO | undefined {
    const vehicle = vehicles.value.find(
      (v) => v.externalId === vehicleExternalId,
    );

    if (!vehicle?.epaFuelEfficiencyId) return undefined;

    return epaFuelEfficiencyRatings.value.find(
      (rating) => rating.id === vehicle.epaFuelEfficiencyId,
    );
  }

  /**
   * Returns the vehicle's boot capacity as display text, derived from its
   * EPA fuel efficiency rating's `luggage_volume_m3` (converted to litres).
   *
   * EPA data has no direct fuel tank size field, but does carry cargo
   * volume, which is what this replaces the previous unimplemented
   * boot-capacity concept with. Returns undefined when no rating has been
   * resolved/fetched yet, or the resolved rating has no luggage volume
   * recorded.
   */
  function getBootCapacityTextForVehicle(
    vehicleExternalId: string,
  ): string | undefined {
    const rating = getEpaFuelEfficiencyRatingForVehicle(vehicleExternalId);
    if (!rating?.luggage_volume_m3) return undefined;

    const litres = Math.round(rating.luggage_volume_m3 * 1000);
    return `${litres}L boot`;
  }

  // --------------------------------- Actions -------------------------------- //

  async function fetchVehicles() {
    fetchState.value = "loading";
    fetchErrorMsg.value = null;

    // A fresh fetch replaces `vehicles.value` with newly-constructed
    // instances that are all hardcoded back to "CUSTOM" by
    // VehicleICE.fromDTO (see that function), so the fake-from-locations
    // sync below needs to be allowed to run again against them.
    hasFakedVehicleSourceFromLocations.value = false;

    const fetchRes = await fetchFleetVehicles();

    if (!fetchRes.success) {
      fetchErrorMsg.value = fetchRes.errorMsg;
      fetchState.value = "error";
      return;
    }

    const resolvedVehicles: Vehicle[] = [];
    const errors: string[] = [];

    for (const dto of fetchRes.data) {
      try {
        resolvedVehicles.push(VehicleCore.fromDTO(dto));
      } catch (error) {
        errors.push(
          error instanceof Error ? error.message : "Unknown vehicle error",
        );
      }
    }

    vehicles.value = resolvedVehicles;

    // See syncVehicleSourceFromLocations - this only actually does anything
    // once vehicle locations have also been fetched (whichever of
    // fetchVehicles/fetchVehicleLocations resolves second is the one that
    // performs the sync; they're kicked off together in appStore.bootstrap).
    syncVehicleSourceFromLocations();

    // EPA ratings are supplementary reference data (real fuel efficiency
    // figures - see VehicleICE.fuelCapacityDisplayText's current "unknown
    // litres" stub), fetched alongside vehicle creation as requested. A
    // failure here is not a vehicle-fetch failure, so it's tracked under
    // its own fetch state rather than affecting fetchState below.
    await fetchEpaFuelEfficiencyRatings();

    if (errors.length) {
      fetchErrorMsg.value = `${errors.length} vehicle(s) could not be loaded`;
      fetchState.value = "partial";
      return;
    }

    fetchState.value = "success";
  }

  /**
   * Fetches the EPA fuel efficiency rating for every vehicle in `vehicles`
   * that has a resolved `epaFuelEfficiencyId`.
   *
   * Every distinct id is batched into a single filtered request rather than
   * one request per vehicle - see fetchEpaFuelEfficiencyRatingsByIds for why
   * this can never become an unfiltered list call against the ~51k-row
   * `epa_fuel_efficiency_ratings` table.
   */
  async function fetchEpaFuelEfficiencyRatings() {
    const epaFuelEfficiencyIds = [
      ...new Set(
        vehicles.value
          .map((vehicle) => vehicle.epaFuelEfficiencyId)
          .filter((id): id is string => id !== null),
      ),
    ];

    epaFuelEfficiencyRatingsFetchState.value = "loading";
    epaFuelEfficiencyRatingsFetchErrorMsg.value = null;

    const fetchRes = await fetchEpaFuelEfficiencyRatingsByIds(
      epaFuelEfficiencyIds,
    );

    if (!fetchRes.success) {
      epaFuelEfficiencyRatingsFetchErrorMsg.value = fetchRes.errorMsg;
      epaFuelEfficiencyRatingsFetchState.value = "error";
      return;
    }

    epaFuelEfficiencyRatings.value = fetchRes.data;
    epaFuelEfficiencyRatingsFetchState.value = "success";
  }

  /**
   * Updates an existing vehicle's editable fields in place.
   *
   * There's no update-vehicle API yet (see CLAUDE.md's OpenAPI note), so
   * this just mutates the local fixture/fetched data after a simulated
   * save delay, mirroring how `userStore.setAndUpdatePrefs` fakes
   * persistence until the real endpoint exists.
   */
  /**
   * Updates a vehicle's editable details, in both the backend and local
   * state. Mirrors userStore.setAndUpdatePrefs: local state is only mutated
   * once the write is confirmed, so a failed save can't leave the UI
   * showing changes that were never actually persisted. Returns whether the
   * update succeeded, so callers can show the right outcome.
   */
  async function updateVehicle(
    externalId: string,
    changes: Partial<
      Pick<
        Vehicle,
        | "name"
        | "licensePlate"
        | "VIN"
        | "fuelType"
        | "gvm"
        | "homeLocationExternalId"
      >
    >,
  ): Promise<boolean> {
    const vehicle = vehicles.value.find((v) => v.externalId === externalId);
    if (!vehicle) return false;

    const payload: VehicleUpdateDTO = {};
    if (changes.name != null) payload.name = changes.name;
    if (changes.licensePlate != null) payload.rego = changes.licensePlate;
    if (changes.VIN != null) payload.vin = changes.VIN;
    if (changes.fuelType != null) payload.fuel_type = changes.fuelType;
    if (changes.gvm != null) payload.gvm = changes.gvm;
    if (changes.homeLocationExternalId != null)
      payload.home_location_id = changes.homeLocationExternalId;

    const res = await updateVehicleAPI(externalId, payload);

    if (!res.success) return false;

    Object.assign(vehicle, changes);
    return true;
  }

  /** fetches the latest known location for every vehicle in the fleet. */
  async function fetchVehicleLocations() {
    vehicleLocationsFetchState.value = "loading";
    vehicleLocationsFetchErrorMsg.value = null;

    const fetchRes = await getAllVehiclesLocations(fleetStore.fleetId ?? undefined);

    if (!fetchRes.success) {
      vehicleLocationsFetchErrorMsg.value = fetchRes.errorMsg;
      vehicleLocationsFetchState.value = "error";
      return;
    }

    vehicleLocations.value = fetchRes.data;
    vehicleLocationsFetchState.value = "success";

    // See syncVehicleSourceFromLocations - this only actually does anything
    // once vehicles have also been fetched (whichever of
    // fetchVehicles/fetchVehicleLocations resolves second is the one that
    // performs the sync; they're kicked off together in appStore.bootstrap).
    // It's a no-op on every subsequent ~10s poll tick after that.
    syncVehicleSourceFromLocations();
  }

  /**
   * PoC-ONLY HACK — DO NOT PORT AS-IS. Fakes each vehicle's `vehicleSource`
   * ("TELEMATICS" vs "CUSTOM") based on whether it has a location record,
   * because the demo backend doesn't yet have real, trustworthy telematics
   * source data on the vehicle row itself - VehicleICE.fromDTO currently
   * hardcodes every fetched vehicle to "CUSTOM" regardless of its actual
   * origin (see that function). Until the backend exposes a real source
   * field, "has a location the telematics-simulation engine is updating" is
   * used as a stand-in signal for "this is a connected/telematics vehicle".
   *
   * This is intentionally only ever applied once per fetched fleet (see
   * hasFakedVehicleSourceFromLocations) rather than on every location poll:
   * which vehicles have locations doesn't change within the demo
   * simulation, so re-deriving this every ~10s would be pointless work, and
   * worse, would risk flipping a vehicle back to "CUSTOM" for one tick if a
   * location fetch ever came back momentarily short a vehicle.
   *
   * Called from the tail of both fetchVehicles and fetchVehicleLocations
   * because those two fetches are kicked off together (see
   * appStore.bootstrap) and can resolve in either order - this makes sure
   * the sync runs exactly once, as soon as both vehicles and locations are
   * actually available, regardless of which one finishes last.
   *
   * DELETE THIS FUNCTION (and its call sites + the flag above) once the
   * backend provides real vehicle source data end-to-end.
   */
  function syncVehicleSourceFromLocations() {
    if (hasFakedVehicleSourceFromLocations.value) return;
    if (vehicles.value.length === 0) return;
    if (vehicleLocationsFetchState.value !== "success") return;

    const vehicleExternalIdsWithLocations = new Set(
      vehicleLocations.value.map((location) => location.vehicle_id),
    );

    for (const vehicle of vehicles.value) {
      vehicle.vehicleSource =
        vehicle.externalId !== null &&
        vehicleExternalIdsWithLocations.has(vehicle.externalId)
          ? "TELEMATICS"
          : "CUSTOM";
    }

    hasFakedVehicleSourceFromLocations.value = true;
  }

  /**
   * Refreshes a single vehicle's location in place, without refetching the
   * whole fleet's locations.
   *
   * `vehicleExternalId` is the vehicle's external id, not a local/domain id.
   */
  async function refreshVehicleLocation(vehicleExternalId: string) {
    const fetchRes = await getVehicleLocation(vehicleExternalId);

    if (!fetchRes.success) {
      vehicleLocationsFetchErrorMsg.value = fetchRes.errorMsg;
      return;
    }

    if (!fetchRes.data) return;

    const index = vehicleLocations.value.findIndex(
      (location) => location.vehicle_id === vehicleExternalId,
    );

    if (index === -1) {
      vehicleLocations.value.push(fetchRes.data);
    } else {
      vehicleLocations.value[index] = fetchRes.data;
    }
  }

  /**
   * Reconciles which vehicles are assigned to a location against the
   * backend's authoritative answer (`vehicles_for_location()`), rather than
   * trusting each vehicle's already-fetched `homeLocationExternalId` -
   * meant to be called when a location's details are opened, or after a
   * change that might affect this location's assignments, to catch another
   * user having reassigned a vehicle since this fleet's vehicles were last
   * fetched.
   *
   * Only updates vehicles already known locally (from `fetchVehicles`) - a
   * vehicle assigned here by another user that this fleet hasn't fetched at
   * all yet is out of scope, same as every other vehicleStore getter/action
   * that reads `vehicles.value` rather than re-fetching the whole fleet.
   *
   * `locationExternalId` is the location's external id, not a local/domain
   * id.
   */
  async function refreshVehiclesForLocation(locationExternalId: string) {
    const fetchRes = await getVehiclesForLocation(locationExternalId);
    if (!fetchRes.success) return;

    const assignedExternalIds = new Set(fetchRes.data.map((v) => v.id));

    for (const vehicle of vehicles.value) {
      if (!vehicle.externalId) continue;

      if (assignedExternalIds.has(vehicle.externalId)) {
        vehicle.homeLocationExternalId = locationExternalId;
      } else if (vehicle.homeLocationExternalId === locationExternalId) {
        // Locally still assigned here, but the backend disagrees - someone
        // else moved/cleared it since our last fetch. The vehicle's actual
        // new home (if any) isn't known until that location is itself
        // refreshed, so this can only clear the stale assignment.
        vehicle.homeLocationExternalId = null;
      }
    }
  }

  /**
   * PoC-ONLY CHOICE — DO NOT PORT AS-IS when this folds back into the main
   * EV app. Starts polling `fetchVehicleLocations()` on a fixed interval, to
   * mirror the demo engine's own ~10s update cadence in the absence of a
   * real push mechanism (see the comment on LOCATIONS_POLL_INTERVAL_MS).
   * The main app should replace this with a real push channel (websocket,
   * SSE, etc.) and should only poll/subscribe while a view that actually
   * shows vehicle locations is open — polling a fleet-wide RPC forever from
   * bootstrap does not scale and is specifically a demo-engine workaround,
   * not a real-world update frequency.
   *
   * Safe to call multiple times: restarts rather than stacking intervals.
   */
  function startLocationsPolling() {
    stopLocationsPolling();

    locationsPollHandle.value = setInterval(() => {
      fetchVehicleLocations();
    }, LOCATIONS_POLL_INTERVAL_MS);
  }

  /** Stops the polling started by `startLocationsPolling`, if running. */
  function stopLocationsPolling() {
    if (locationsPollHandle.value === null) return;

    clearInterval(locationsPollHandle.value);
    locationsPollHandle.value = null;
  }

  function $reset() {
    fetchState.value = "idle";
    vehicles.value = [];

    vehicleLocationsFetchState.value = "idle";
    vehicleLocationsFetchErrorMsg.value = null;
    vehicleLocations.value = [];
    hasFakedVehicleSourceFromLocations.value = false;

    epaFuelEfficiencyRatingsFetchState.value = "idle";
    epaFuelEfficiencyRatingsFetchErrorMsg.value = null;
    epaFuelEfficiencyRatings.value = [];

    stopLocationsPolling();
  }

  return {
    // --------------------------------- State -------------------------------- //

    /** reactive array to hold the vehicles fetched from the API */
    vehicles,

    /** indicates the vehicle store's current data fetching state */
    fetchState,

    /** reactive array holding each vehicle's latest known location */
    vehicleLocations,

    /** indicates the vehicle locations fetch's current state */
    vehicleLocationsFetchState,

    /** reactive array holding the fetched EPA fuel efficiency ratings relevant to the current fleet */
    epaFuelEfficiencyRatings,

    /** indicates the EPA fuel efficiency ratings fetch's current state */
    epaFuelEfficiencyRatingsFetchState,

    // -------------------------------- Getters ------------------------------- //

    /** helper flag to indicate async fetching is in operation. Good for loading animations. */
    isFetching,

    /** computed property to get the default vehicle */
    defaultVehicle,

    /**
     * Computed property to get connected vehicles.
     *
     * Connected vehicles are created from telematics data.
     */
    getConnectedVehicles,

    /**
     * Computed property to get custom vehicles.
     *
     * Custom vehicles are created by the user.
     */
    getCustomVehicles,

    /**
     * Computed property to get generic vehicles.
     *
     * Generic vehicles are a bear minimum vehicle created from a models physics
     * profile. This is nothing more than a wrapper to support route planning
     * without having to create a full vehicle record. Generic vehicles are not
     * persisted and are not linked to any real world vehicle.
     */
    getGenericVehicles,

    /** helper flag to indicate the vehicle locations fetch is in operation. Good for loading animations. */
    isFetchingLocations,

    /** Returns the latest known location for the given vehicle, if one has been fetched. */
    getLocationForVehicle,

    /** Looks up a vehicle by its external id. */
    getVehicleByExternalId,

    /** Returns the EPA fuel efficiency rating for the given vehicle, if resolved and fetched. */
    getEpaFuelEfficiencyRatingForVehicle,

    /** Returns the vehicle's boot capacity as display text, derived from its EPA rating's luggage volume. */
    getBootCapacityTextForVehicle,

    // -------------------------------- Actions ------------------------------- //

    /** fetches the fleets vehicles from the backend and assigns them to state. */
    fetchVehicles,

    /** updates an existing vehicle's editable fields. */
    updateVehicle,

    /** fetches the latest known location for every vehicle in the fleet. */
    fetchVehicleLocations,

    /** fetches the EPA fuel efficiency rating for every vehicle in the fleet that has one resolved. */
    fetchEpaFuelEfficiencyRatings,

    /** Refreshes a single vehicle's location in place, without refetching the whole fleet's locations. */
    refreshVehicleLocation,

    /** Reconciles which already-fetched vehicles are assigned to a location against the backend's authoritative answer. */
    refreshVehiclesForLocation,

    /**
     * PoC-ONLY, DO NOT PORT AS-IS: starts polling fetchVehicleLocations on a
     * fixed interval. See the function's own doc comment for why.
     */
    startLocationsPolling,

    /** Stops the polling started by startLocationsPolling, if running. */
    stopLocationsPolling,

    /** Resets the store to its initial state. */
    $reset,
  };
});
